import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import Widget from '@/components/common/widget';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SubscriptionPlan, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { getOriginEventId } from '@/helpers/event.helper';
import { resolveRefId } from '@/helpers/object.id.helper';
import {
  findBookedEventSubscription,
  findEventCartLine,
  tierFromEventSubscription,
} from '@/helpers/event-subscription-status';
import {
  addonParentMatchesContextLine,
  resolveEventPlanWidgetContext,
  type EventPlanLineDef,
  type EventPlanWidgetSurface,
} from '@/constants/event-plan-widget-contexts';
import EventManagePlanDialog from '@/components/events/plan/event-manage-plan-dialog';
import EventSubscriptionCheckoutModal from '@/components/events/plan/event-subscription-checkout.modal';
import { useEventSubscriptionCheckoutCartQuery } from '@/state/queries/transactions/use-event-subscription-checkout-cart';
import { usePatchEventSubscriptionCheckoutCart } from '@/state/mutations/transactions/use-patch-event-subscription-checkout-cart';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans';
import { useNotifications } from '@/hooks/use-notifications';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import BasePlanCapacityUsage from '@/components/flows/detail/flow-detail-tabs/base-plan-capacity-usage';
import { resolveProductClaimText } from '@/helpers/resolve-translated-product-field';
import { EVENT_SUBSCRIPTION_PRODUCT_KEYS } from '@/constants/event-subscription-product-keys';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';

function translateTierLabel(
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
  tier: string,
): string {
  const k = `subscription.organization.tiers.${tier}` as TranslationKey;
  const r = t(k);
  return r === k ? tier : r;
}

/** Plan-wide claim for widget captions (never tier-specific). */
function resolvePlanLineExplainer(
  t: (key: string, params?: Record<string, string | number>) => string,
  productKey: string,
  explainerFallbackKey: TranslationKey,
): string {
  const claim =
    resolveProductClaimText(t, productKey) ??
    // Multi-flow capacity lines share the single-flow base_capacity plan claim when unset.
    (productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT ||
    productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW
      ? resolveProductClaimText(t, EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY)
      : undefined);
  if (claim) {
    return claim;
  }
  return t(explainerFallbackKey);
}

const chipSx = { height: 22 } as const;

function PlanSection({
  title,
  explainer,
  bookedTier,
  cartTier,
  action,
  below,
  t,
}: {
  title: string;
  explainer?: string;
  bookedTier: SubscriptionTier | null;
  cartTier: string | null | undefined;
  action: ReactNode;
  below?: ReactNode;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}) {
  const cart = cartTier?.trim() || null;

  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
        {title}
      </Typography>
      {explainer ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {explainer}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75} minWidth={0} flex={1}>
          <Typography variant="body2" color="text.secondary">
            {bookedTier
              ? t('event.plan.statusCurrent', { tier: translateTierLabel(t, bookedTier) })
              : t('event.plan.noPlanActive')}
          </Typography>
          {cart ? (
            <Chip
              size="small"
              label={t('event.plan.cartTierChip', { tier: translateTierLabel(t, cart) })}
              color="success"
              variant="outlined"
              sx={chipSx}
            />
          ) : null}
        </Stack>
        {action}
      </Stack>
      {below}
    </Stack>
  );
}

function resolveLineScope(
  line: EventPlanLineDef,
  ids: { eventId: string; flowId?: string; eventSlotId?: string | null },
): { scopeType: string; scopeId: string } | null {
  if (line.scopeIdFrom === 'event') {
    return { scopeType: 'Event', scopeId: ids.eventId };
  }
  if (line.scopeIdFrom === 'flow') {
    if (!ids.flowId) return null;
    return { scopeType: 'Flow', scopeId: ids.flowId };
  }
  // eventSlotOrFlow — broadcast
  if (ids.eventSlotId) {
    return { scopeType: 'EventSlot', scopeId: ids.eventSlotId };
  }
  if (!ids.flowId) return null;
  return { scopeType: 'Flow', scopeId: ids.flowId };
}

function addonDisplayName(
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
  productKey: string,
): string {
  if (productKey === 'stage_ready') return t('event.plan.stageReadyLine');
  return productKey;
}

export default function EventSubscriptionPlanWidget({
  surface,
  flow,
  eventSlotId,
}: {
  surface: EventPlanWidgetSurface;
  /** Required for flow surface (capacity usage / Flow scopes). Optional on event surface. */
  flow?: FlowDto;
  eventSlotId?: string | null;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');
  const monetizeEnabled = !monetizeAccessLoading && hasMonetizeAccess;
  const { data: permissions } = usePermissionsQuery();
  const event = useEventStore((s) => s.event);
  const eventId = getOriginEventId(event);
  const flowId = flow ? (resolveRefId(flow.origin) ?? flow.id) : undefined;
  const isMultiFlow = event?.multiFlow === true;
  const canUpdateSubscriptionPlan = Boolean(
    eventId && permissions?.events?.[eventId]?.includes(EVENT_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN),
  );
  /** Cart/checkout APIs require update_subscription_plan — do not call them without it (avoids prod 403 noise). */
  const canManageCheckoutCart = monetizeEnabled && canUpdateSubscriptionPlan;

  const context = useMemo(
    () => resolveEventPlanWidgetContext({ surface, multiFlow: isMultiFlow }),
    [surface, isMultiFlow],
  );

  const { data: cartData } = useEventSubscriptionCheckoutCartQuery(eventId, canManageCheckoutCart);
  const { data: plansData } = useSubscriptionPlansQuery({
    entityType: 'Event',
    enabled: monetizeEnabled && Boolean(eventId),
  });
  const patchCart = usePatchEventSubscriptionCheckoutCart(eventId);

  const [manageKey, setManageKey] = useState<string | null>(null);
  const [manageScope, setManageScope] = useState<{ scopeType: string; scopeId: string } | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const cartLines = cartData?.cart?.lines;
  const subscriptions = event?.subscriptions;

  const resolvedLines = useMemo(() => {
    if (!context || !eventId) return [];
    return context.lines
      .map((line) => {
        const scope = resolveLineScope(line, { eventId, flowId, eventSlotId });
        if (!scope) return null;
        return { line, ...scope };
      })
      .filter((row): row is { line: EventPlanLineDef; scopeType: string; scopeId: string } => row != null);
  }, [context, eventId, flowId, eventSlotId]);

  /**
   * Product keys present in the public catalog after server visibility gates.
   * Whole-line gates (`plan_{productKey}`) remove every tier — those lines must not appear in the widget.
   */
  const catalogVisibleProductKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const plan of plansData?.plans ?? []) {
      if (plan.billingPeriod !== 'ONCE') continue;
      const pk = plan.subscriptionProductKey?.trim();
      if (pk) keys.add(pk);
    }
    return keys;
  }, [plansData?.plans]);

  /** Hide feature-gated product lines; keep a line if the event already booked it. */
  const visibleResolvedLines = useMemo(() => {
    return resolvedLines.filter(({ line, scopeType, scopeId }) => {
      const booked = Boolean(findBookedEventSubscription(subscriptions, line.productKey, scopeType, scopeId));
      // Until catalog loads, only show booked lines — never flash a gated empty section.
      if (!plansData) return booked;
      if (catalogVisibleProductKeys.has(line.productKey)) return true;
      return booked;
    });
  }, [plansData, resolvedLines, catalogVisibleProductKeys, subscriptions]);

  const parentKeys = useMemo(() => visibleResolvedLines.map((r) => r.line.productKey), [visibleResolvedLines]);

  /** One catalog representative per add-on product key attached to a visible context parent line. */
  const addonDefs = useMemo(() => {
    const catalogPlans = plansData?.plans ?? [];
    const byKey = new Map<string, { productKey: string; plan: SubscriptionPlan; parentLineKey: string }>();
    for (const plan of catalogPlans) {
      if (!plan.requiresParent || plan.billingPeriod !== 'ONCE') continue;
      const pk = plan.subscriptionProductKey?.trim();
      const parent = plan.parentProductKey?.trim();
      if (!pk || !parent) continue;
      const parentLineKey = parentKeys.find((lineKey) => addonParentMatchesContextLine(parent, lineKey));
      if (!parentLineKey) continue;
      const existing = byKey.get(pk);
      if (!existing || (plan.tier === 'BASIC' && existing.plan.tier !== 'BASIC')) {
        byKey.set(pk, { productKey: pk, plan, parentLineKey });
      }
    }
    return [...byKey.values()];
  }, [plansData?.plans, parentKeys]);

  const openManage = (productKey: string, scopeType: string, scopeId: string) => {
    setManageKey(productKey);
    setManageScope({ scopeType, scopeId });
  };

  const toggleAddon = async (params: {
    productKey: string;
    scopeType: string;
    scopeId: string;
    enabled: boolean;
    tier?: string;
  }) => {
    if (!eventId) return;
    const other =
      cartLines?.filter(
        (l) =>
          l.subscriptionProductKey !== params.productKey ||
          l.scopeType !== params.scopeType ||
          l.scopeId !== params.scopeId,
      ) ?? [];
    try {
      await patchCart.mutateAsync({
        lines: params.enabled
          ? [
              ...other.map((l) => ({
                subscriptionProductKey: l.subscriptionProductKey,
                tier: l.tier,
                billingPeriod: 'ONCE' as const,
                additionalQuotaUnits: l.additionalQuotaUnits,
                scopeType: l.scopeType,
                scopeId: l.scopeId,
              })),
              {
                subscriptionProductKey: params.productKey,
                tier: params.tier ?? 'BASIC',
                billingPeriod: 'ONCE',
                scopeType: params.scopeType,
                scopeId: params.scopeId,
              },
            ]
          : other.map((l) => ({
              subscriptionProductKey: l.subscriptionProductKey,
              tier: l.tier,
              billingPeriod: 'ONCE' as const,
              additionalQuotaUnits: l.additionalQuotaUnits,
              scopeType: l.scopeType,
              scopeId: l.scopeId,
            })),
      });
    } catch {
      notification.notifyError(t('event.plan.cartSaveError'));
    }
  };

  if (!monetizeEnabled || !eventId || !context) return null;
  // Whole-line gates can leave no visible products for this surface.
  if (visibleResolvedLines.length === 0 && addonDefs.length === 0) return null;

  const hasCart = Boolean(cartLines?.length);

  return (
    <>
      <Widget title={t(context.titleKey)}>
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {t(context.hintKey)}
          </Typography>

          {visibleResolvedLines.map(({ line, scopeType, scopeId }, index) => {
            const booked = findBookedEventSubscription(subscriptions, line.productKey, scopeType, scopeId);
            const cart = findEventCartLine(cartLines, line.productKey, scopeType, scopeId);
            const bookedTier = tierFromEventSubscription(booked);

            return (
              <Fragment key={`${line.productKey}|${scopeType}|${scopeId}`}>
                {index > 0 ? <Divider /> : null}
                <PlanSection
                  title={t(line.titleKey)}
                  explainer={resolvePlanLineExplainer(t, line.productKey, line.explainerKey)}
                  bookedTier={bookedTier}
                  cartTier={cart?.tier}
                  t={t}
                  action={
                    canManageCheckoutCart ? (
                      <Button size="small" onClick={() => openManage(line.productKey, scopeType, scopeId)}>
                        {bookedTier || cart?.tier ? t('event.plan.manage') : t('event.plan.add')}
                      </Button>
                    ) : null
                  }
                  below={
                    line.capacityUsage !== 'none' && scopeId ? (
                      <BasePlanCapacityUsage
                        flow={flow}
                        capacityScopeType={scopeType}
                        capacityScopeId={scopeId}
                        flowScopeId={flowId ?? ''}
                        usageMode={line.capacityUsage}
                      />
                    ) : null
                  }
                />
              </Fragment>
            );
          })}

          {addonDefs.length > 0 ? (
            <>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
                  {t('event.plan.addonsSection')}
                </Typography>
                {addonDefs.map((addon) => {
                  const parentResolved = visibleResolvedLines.find((r) => r.line.productKey === addon.parentLineKey);
                  if (!parentResolved) return null;
                  const { scopeType, scopeId } = parentResolved;
                  const booked = findBookedEventSubscription(subscriptions, addon.productKey, scopeType, scopeId);
                  const cart = findEventCartLine(cartLines, addon.productKey, scopeType, scopeId);
                  const parentBooked = findBookedEventSubscription(
                    subscriptions,
                    parentResolved.line.productKey,
                    scopeType,
                    scopeId,
                  );
                  const parentCart = findEventCartLine(cartLines, parentResolved.line.productKey, scopeType, scopeId);
                  const hasParent = Boolean(parentBooked || parentCart);
                  const addonClaim = resolveProductClaimText(t, addon.productKey);

                  return (
                    <Box
                      key={addon.productKey}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack spacing={0.25} minWidth={0} flex={1}>
                          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {addonDisplayName(t, addon.productKey)}
                            </Typography>
                            <Chip
                              size="small"
                              label={t('subscription.organization.planPage.addOnBadge')}
                              color="primary"
                              variant="outlined"
                              sx={chipSx}
                            />
                            {booked ? (
                              <Chip
                                size="small"
                                label={t('event.plan.statusCurrentAddon')}
                                color="default"
                                variant="outlined"
                                sx={chipSx}
                              />
                            ) : null}
                            {cart ? (
                              <Chip
                                size="small"
                                label={t('subscription.organization.planPage.addonInCheckoutCart')}
                                color="success"
                                variant="outlined"
                                sx={chipSx}
                              />
                            ) : null}
                          </Stack>
                          {addonClaim ? (
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                              {addonClaim}
                            </Typography>
                          ) : null}
                          {typeof addon.plan.price === 'number' ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('event.plan.addonPriceOnce', { amount: addon.plan.price.toFixed(2) })}
                            </Typography>
                          ) : null}
                        </Stack>
                        {canManageCheckoutCart && !(booked && !cart) ? (
                          <Button
                            size="small"
                            disabled={!hasParent && !cart}
                            onClick={() => {
                              if (cart) {
                                void toggleAddon({
                                  productKey: addon.productKey,
                                  scopeType,
                                  scopeId,
                                  enabled: false,
                                });
                                return;
                              }
                              void toggleAddon({
                                productKey: addon.productKey,
                                scopeType,
                                scopeId,
                                enabled: true,
                                tier: addon.plan.tier,
                              });
                            }}
                            sx={{ flexShrink: 0, fontWeight: 600 }}
                          >
                            {cart ? t('event.plan.remove') : t('event.plan.add')}
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          ) : null}

          {canManageCheckoutCart ? (
            <Button variant="contained" onClick={() => setCheckoutOpen(true)} disabled={!hasCart}>
              {t('event.plan.openCheckout')}
            </Button>
          ) : null}
        </Stack>
      </Widget>

      {canManageCheckoutCart && manageKey && manageScope ? (
        <EventManagePlanDialog
          open
          onClose={() => {
            setManageKey(null);
            setManageScope(null);
          }}
          onGoToCart={() => {
            setManageKey(null);
            setManageScope(null);
            setCheckoutOpen(true);
          }}
          eventId={eventId}
          subscriptionProductKey={manageKey}
          scopeType={manageScope.scopeType}
          scopeId={manageScope.scopeId}
        />
      ) : null}

      {canManageCheckoutCart ? (
        <EventSubscriptionCheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} eventId={eventId} />
      ) : null}
    </>
  );
}
